import { IAnalyticsModelResponse } from '@kcftech/prometheustypescriptapiclient';
import {
    Dropdown,
    DropdownItemRenderer,
    DropdownLabelRenderer,
} from 'components/dropdown/Dropdown';
import { useAnalyticsModelInfiniteDataSource } from 'custom_hooks/data_hooks/data_source/analyticsModelDataSource';
import { useEffect } from 'react';
import { getId } from 'utils/data';

const getModelName = ({ Name, Corporation, Version }: IAnalyticsModelResponse) => {
    const name = Corporation ? Name : `${Name} (KCF)`;
    const version = `v${Version}`;

    return `${name} ${version}`;
};

const renderLabel: DropdownLabelRenderer<IAnalyticsModelResponse> = ({ value }) =>
    value ? getModelName(value) : 'Select a model';

const renderItem: DropdownItemRenderer<IAnalyticsModelResponse> = ({ item }) =>
    getModelName(item);

export type AnalyticsModelDropdownProps = Readonly<{
    corporationId?: string;
    locationId?: string;
    value: IAnalyticsModelResponse | undefined;
    onChange: (newValue: IAnalyticsModelResponse) => void;
}>;

export function AnalyticsModelDropdown({
    corporationId,
    locationId,
    value,
    onChange,
}: AnalyticsModelDropdownProps) {
    const {
        filter,
        setFilter,
        results,
        isLoading,
        hasNext,
        loadNext,
    } = useAnalyticsModelInfiniteDataSource({
        name: 'analytics-model-dropdown',
        defaultFilter: {
            corporationId,
            locationId,
            includeGlobal: true,
        },
    });

    useEffect(() => setFilter((current) => ({ ...current, corporationId, locationId })), [
        corporationId,
        locationId,
        setFilter,
    ]);

    return (
        <Dropdown<IAnalyticsModelResponse>
            dataKey={getId}
            data={{
                items: results,
                isLoading,
                hasMore: hasNext ?? false,
                onLoadMore: loadNext,
            }}
            value={value}
            renderLabel={renderLabel}
            renderItem={renderItem}
            filterable={{
                placeholder: 'Search models',
                filterText: filter.nameSearch ?? '',
                onChange: (nameSearch) =>
                    setFilter((current) => ({ ...current, nameSearch })),
            }}
            onChange={onChange}
        />
    );
}
